import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

/** Material */
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatBadgeModule } from '@angular/material/badge';

/** 3rd Party */
import { TranslateModule, TranslateService } from '@ngx-translate/core';

/** Components */
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';

/** Services */
import { AuthService } from 'src/app/core/services/auth.service';
import { CustomizerSettingsService } from 'src/app/core/services/customizer-settings.service';
import { ToggleService } from 'src/app/core/services/toggle.service';
import { NotificationsService } from 'src/app/components/notifications/notifications.service';
import { NotificationDataService } from 'src/app/components/notifications/notification-data.service';
import { InstitutionNotificationService } from 'src/app/components/notifications/institution-notification.service';
import { I18nService } from 'src/app/core/services/i18n.service';

/** Constants */
import { ROUTES } from 'src/app/shared/constants/app-routes';
import { AppNotification } from 'src/app/components/notifications/notification.model';
import { environment } from 'src/environments/environment';
import { TourUiService } from 'src/app/core/services/tourui.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { of, Subject, Subscription, switchMap, tap } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { userTypes } from 'src/app/shared/constants/userType';
import {
  OnlineStatusModule,
  OnlineStatusService,
  OnlineStatusType,
} from 'ngx-online-status';
import { AlertService } from 'src/app/core/services/alert.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MatButtonModule,
    MatMenuModule,
    MatDialogModule,
    MatBadgeModule,
    OnlineStatusModule,
  ],
  providers: [],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  ROUTES = ROUTES;
  userProfile: any;
  userType: string = '';
  notificationCount: number = 0;
  notifications: AppNotification[] = [];
  private importFeedbackSub?: Subscription;
  isToggled = false;
  supportedLanguages = environment.languageConfig.supportedLanguages;
  userTypes = userTypes;
  personId$ = new Subject<string>();
  isNumber: boolean = false;

  status: OnlineStatusType = this.onlineStatusService.getStatus(); // Enum provided by ngx-online-status
  onlineStatusCheck: any = OnlineStatusType;

  constructor(
    public dialog: MatDialog,
    private authService: AuthService,
    private toggleService: ToggleService,
    public themeService: CustomizerSettingsService,
    private notificationsService: NotificationsService,
    private notificationDataService: NotificationDataService,
    private institutionNotificationService: InstitutionNotificationService,
    public i18nService: I18nService,
    public tourUiService: TourUiService,
    public permissionsService: PermissionService,
    private router: Router,
    private onlineStatusService: OnlineStatusService,
    private alertService: AlertService,
    private translateService: TranslateService
  ) {
    this.onlineStatusService.status.subscribe((status: OnlineStatusType) => {
      // Retrieve Online status Type
      this.status = status;

      if (this.status === this.onlineStatusCheck.OFFLINE) {
        this.alertService.alert({
          type: 'Error',
          message: 'You are offline. Please check your internet connection.',
        });
      }
    });
    this.toggleService.isToggled$.subscribe((isToggled) => {
      this.isToggled = isToggled;
    });

    this.authService.profile$.subscribe((profile) => {
      if (!this.authService.isLoggedIn()) {
        this.router.navigate(['/']);
      }
      if (profile) {
        this.userProfile = profile;
        this.userType = profile.attributes?.userType[0];
        if (
          this.userType != null &&
          this.userType.toString().trim() !== '' &&
          !isNaN(Number(this.userType))
        ) {
          this.isNumber = true;
        } else {
          this.isNumber = false;
        }
        this.personId$.next(this.userProfile?.attributes.personId[0]);
        //this.loadUnreadNotificationsCount();

        // Institution accounts subscribe to the student-import notification hub.
        if (this.userType === 'Institution' || this.userType === '2') {
          this.institutionNotificationService.start(this.userProfile?.id);
        }
      }
    });
  }

  ngOnInit(): void {
    this.loadUnreadNotificationsCount();
    // this.loadUserDetails();
    if (this.userProfile?.id) {
      this.loadNotifications();
    }

    this.importFeedbackSub =
      this.institutionNotificationService.importFeedback$.subscribe(
        (notification) => this.onImportFeedback(notification)
      );
  }

  ngOnDestroy(): void {
    this.importFeedbackSub?.unsubscribe();
    this.institutionNotificationService.stop();
  }

  /** Handles a real-time student-import feedback notification from the hub. */
  private onImportFeedback(notification: AppNotification): void {
    if (!notification) {
      return;
    }

    // Toast (only 'Success' renders as a non-error snackbar).
    this.alertService.alert({ type: 'Success', message: notification.message });

    // Show immediately at the top of the header dropdown.
    this.notifications = [notification, ...this.notifications];

    // Bump the unread badge.
    this.notificationCount = (this.notificationCount || 0) + 1;
    this.notificationDataService.incrementNotificationCount();

    // Sync with any backend-persisted copy.
    if (this.userProfile?.id) {
      this.loadNotifications();
      this.personId$.next(this.userProfile?.attributes?.personId?.[0]);
    }
  }

  switchLanguage(language: string): void {
    this.i18nService.language = language;
  }

  private loadNotifications(): void {
    this.notificationsService
      .getNotifications(this.userProfile?.attributes?.personId[0], 1, 5, true)
      .subscribe((res) => {
        this.notifications = res.body || [];
      });
  }

  private loadUnreadNotificationsCount(): void {
    this.personId$
      .pipe(
        debounceTime(3000),
        distinctUntilChanged(),
        switchMap((userId) =>
          userId
            ? this.notificationsService.getUnreadNotificationsCount(userId)
            : of(0)
        )
      )
      .subscribe((res: number) => {
        this.notificationCount = res;
        this.notificationDataService.updateNotificationCount(res);
      });
  }

  private loadUserDetails(): void {
    //this.userProfile = this.authService.getUserProfile();
  }

  MsLogout() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        heading: 'Confirm Logout',
        dialogContext: 'Are you sure you want to logout?',
        type: 'Basic',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.authService.logout();
      }
    });
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  toggle() {
    this.toggleService.toggle();
  }

  toggleSidebarTheme() {
    this.themeService.toggleSidebarTheme();
  }

  toggleHideSidebarTheme() {
    this.themeService.toggleHideSidebarTheme();
  }

  toggleCardBorderTheme() {
    this.themeService.toggleCardBorderTheme();
  }

  toggleHeaderTheme() {
    this.themeService.toggleHeaderTheme();
  }

  toggleCardBorderRadiusTheme() {
    this.themeService.toggleCardBorderRadiusTheme();
  }

  toggleRTLEnabledTheme() {
    this.themeService.toggleRTLEnabledTheme();
  }

  toggleConnectionStatus() {
    // This method can be used to manually refresh connection status
    // or show connection details
    if (this.status === this.onlineStatusCheck.OFFLINE) {
      this.alertService.alert({
        type: 'Warning',
        message:
          'You are currently offline. Please check your internet connection.',
      });
    } else {
      this.alertService.alert({
        type: 'Success',
        message: 'You are online and connected to the internet.',
      });
    }
  }
}
